// Phase Lang-A.7 — Reusable "liquid glass" language dropdown.
//
// Used by the public invite page, the auth pages (Login, Register), and any
// authenticated page that lets the user switch UI language. Centralises the
// translucent glass styling so future tweaks land everywhere at once.
//
// Props:
//   value           current language code (e.g. 'en', 'pt')
//   onValueChange   (lang) => void  — called when user picks a new language
//   options         [{ code, label }, ...]  — list of options to render
//   className       extra classes on the SelectTrigger (optional)
//   testId          data-testid for the trigger (default: 'language-select')
//   width           Tailwind width class (default: 'w-[140px]')

import React from 'react';
import { Globe } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

const GLASS_TRIGGER_CLASS =
  'border border-white/40 bg-white/30 backdrop-blur-xl hover:bg-white/40 transition-all shadow-[0_4px_20px_rgba(0,0,0,0.08)]';

const GLASS_TRIGGER_STYLE = {
  color: '#5a5a5a',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  backdropFilter: 'blur(20px) saturate(180%)',
};

const GLASS_CONTENT_STYLE = {
  backgroundColor: 'rgba(255,255,255,0.55)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
  backdropFilter: 'blur(24px) saturate(180%)',
};

const GLASS_ITEM_STYLE = { color: '#5a5a5a' };

export default function LiquidGlassLanguageSelect({
  value,
  onValueChange,
  options = [],
  className = '',
  testId = 'language-select',
  width = 'w-[140px]',
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className={`${width} ${GLASS_TRIGGER_CLASS} ${className}`}
        style={GLASS_TRIGGER_STYLE}
        data-testid={testId}
      >
        <Globe className="h-4 w-4 mr-2" style={{ color: '#5a5a5a' }} />
        <SelectValue />
      </SelectTrigger>
      <SelectContent
        className="border border-white/40 shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
        style={GLASS_CONTENT_STYLE}
      >
        {options.map((opt) => (
          <SelectItem
            key={opt.code}
            value={opt.code}
            className="focus:bg-white/60"
            style={GLASS_ITEM_STYLE}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
